/**
 * The home hero's screen journeys (#2490). Each screen acts out the real
 * workflow on captures of the Harbourline demo team: a camera zooms in on a
 * control, holds, pans while zoomed, taps (a ripple), zooms out, and the next
 * page slides or fades in. One loop of LOOP_S seconds drives every screen; the
 * day-to-night sky and the bubbles in the stylesheet run on the same loop.
 * The tracks are timed together so the coordination reads across screens.
 * Morning: the lead sets the gameplan while Nicole submits leave (12 s), which
 * lands on the lead's laptop and phone (13.6 s); the lead accepts it. Day: the
 * lead solves, moves one shift and publishes (39 s). Evening: Gary reports
 * off sick for the 22nd (48 s); the lead seats Hakim. Night: every phone is told.
 *
 * Positions are fractions of the capture (0 to 1), taken from the boxes in
 * `media/screenshots/hero-journey-manifest.json` or measured on the
 * captures. Only transform and opacity animate (#2470).
 */

export const LOOP_S = 72;

const EASE = "cubic-bezier(0.45, 0, 0.25, 1)";

export type Page = { src: string; alt: string };

/** A camera waypoint: zoom `s` with the point (fx, fy) of the capture at the view centre. */
export type Cam = { t: number; s: number; fx: number; fy: number };

/** At time t, `page` comes in over the page before it. */
export type Cut = { t: number; page: Page; how: "fade" | "slide" | "rise"; still?: boolean };

/** A tap ripple at (fx, fy) of the capture. */
export type Tap = { t: number; fx: number; fy: number };

export type Track = {
  /** Capture size in px; sets the camera's aspect ratio. */
  width: number;
  height: number;
  /** Visible width as a fraction of the camera (phones crop a few percent on the right). */
  view: number;
  cams: ReadonlyArray<Cam>;
  cuts: ReadonlyArray<Cut>;
  taps: ReadonlyArray<Tap>;
};

const J = "/site/harbourline/journey";
const p = (file: string, alt: string): Page => ({ src: `${J}/${file}.webp`, alt });

const PH = { width: 780, height: 1688, view: 0.965 } as const;
const LD = { width: 1100, height: 825, view: 1 } as const;

/** Phone point from capture px. */
const ph = (x: number, y: number) => ({ fx: x / 780, fy: y / 1688 });
/** Lead point from capture px. */
const ld = (x: number, y: number) => ({ fx: x / 1100, fy: y / 825 });

const OUT = { s: 1, fx: 0.5, fy: 0.5 } as const;

/**
 * Builds waypoints from a script: `go(t, dur, target)` moves the camera from
 * where it is to `target` between t and t + dur; it holds in between moves.
 */
function camera(script: ReadonlyArray<readonly [number, number, Omit<Cam, "t">]>): Cam[] {
  const out: Cam[] = [{ t: 0, ...OUT }];
  let cur: Omit<Cam, "t"> = OUT;
  for (const [t, dur, target] of script) {
    out.push({ t, ...cur }, { t: t + dur, ...target });
    cur = target;
  }
  out.push({ t: LOOP_S, ...OUT });
  return out;
}

/* Nicole (member): a leave request; the lead's laptop and phone receive it at once. */
const X = {
  tasks: p("x-01-tasks", "A member's Tasks page with + Request leave"),
  form: p("x-02-form", "The leave request form with an October calendar"),
  d20: p("x-03-day-20", "The leave form with 20 October picked"),
  d21: p("x-04-days-20-21", "The leave form with 20 to 21 October picked"),
  submit: p("x-05-submit", "The filled leave form with the Submit leave button"),
  sent: p("x-06-sent", "The leave request sent: Leave, 20 to 21 October"),
  inbox: p(
    "x-08-inbox",
    "The inbox: leave confirmed, and your shifts for 12 to 25 October are published",
  ),
  open: p("x-09-inbox-open", "The inbox row opened, with Open calendar"),
  shifts: p(
    "x-10-calendar",
    "The member's calendar with their shifts and leave on 20 and 21 October",
  ),
};

export const MEMBER_1: Track = {
  ...PH,
  cuts: [
    { t: 0, page: X.tasks, how: "fade" },
    { t: 3.4, page: X.form, how: "slide" },
    { t: 6.0, page: X.d20, how: "fade" },
    { t: 8.3, page: X.d21, how: "fade" },
    { t: 9.6, page: X.submit, how: "rise" },
    { t: 12.7, page: X.sent, how: "slide" },
    { t: 59.0, page: X.inbox, how: "fade" },
    { t: 61.6, page: X.open, how: "fade" },
    { t: 64.3, page: X.shifts, how: "slide", still: true },
    { t: 71.5, page: X.tasks, how: "fade" },
  ],
  cams: camera([
    [0.8, 0.6, { s: 2.2, ...ph(130, 456) }], // + Request leave
    [3.0, 0.5, OUT],
    [3.8, 0.6, { s: 2.4, ...ph(185, 579) }], // day 20
    [6.2, 0.5, { s: 2.4, ...ph(288, 579) }], // pan to day 21, still zoomed
    [8.9, 0.5, OUT],
    [9.9, 0.7, { s: 2.0, ...ph(390, 1532) }], // Submit leave
    [12.2, 0.4, OUT],
    [13.0, 0.7, { s: 1.25, ...ph(390, 232) }], // the sent card
    [30.0, 0.9, OUT],
    [59.3, 0.7, { s: 1.45, ...ph(320, 534) }], // your shifts are published
    [61.8, 0.5, { s: 1.9, ...ph(180, 662) }], // pan to Open calendar
    [64.0, 0.3, OUT],
    [64.6, 0.7, { s: 2.2, ...ph(224, 766) }], // leave on 20 and 21 Oct
    [70.6, 0.5, OUT],
  ]),
  taps: [
    { t: 2.8, ...ph(130, 456) },
    { t: 5.8, ...ph(185, 579) },
    { t: 8.1, ...ph(288, 579) },
    { t: 12.0, ...ph(390, 1532) },
    { t: 61.4, ...ph(320, 534) },
    { t: 63.8, ...ph(180, 662) },
  ],
};

/*
 * Second phone: Gary reports off sick for the 22nd after the roster is
 * published; at night the phone belongs to Hakim, who is told he now covers
 * that day (the avatar turns from S to H in the stylesheet).
 */
const S = {
  tasks: p("s-01-tasks", "A member's Tasks page with + Report off sick"),
  form: p("s-02-form", "The sick report form with Sick day chosen"),
  day: p("s-03-day-22", "The sick report with 22 October picked"),
  submit: p("s-04-submit", "The sick report with the Report off sick button"),
  sent: p("s-05-sent", "The sick report sent: Off sick, 22 October"),
  told: p(
    "cv-01-told",
    "Another member's inbox: 2 changes to your roster, Thu 22 Oct now North Gate PM",
  ),
  cover: p("cv-02-calendar", "That member's calendar with North Gate on 22 October"),
};

export const MEMBER_2: Track = {
  ...PH,
  cuts: [
    { t: 0, page: S.tasks, how: "fade" },
    { t: 42.6, page: S.form, how: "slide" },
    { t: 45.2, page: S.day, how: "fade" },
    { t: 46.0, page: S.submit, how: "rise" },
    { t: 48.5, page: S.sent, how: "slide" },
    { t: 59.2, page: S.told, how: "fade" },
    { t: 61.8, page: S.cover, how: "slide", still: true },
    { t: 71.5, page: S.tasks, how: "fade" },
  ],
  cams: camera([
    [2.0, 1.0, { s: 1.4, ...ph(390, 600) }], // his settled requests
    [20.0, 1.0, OUT],
    [40.4, 0.6, { s: 2.2, ...ph(139, 440) }], // + Report off sick
    [43.0, 0.6, { s: 2.4, ...ph(389, 580) }], // day 22
    [45.4, 0.5, OUT],
    [46.3, 0.6, { s: 2.0, ...ph(390, 1486) }], // Report off sick
    [48.8, 0.7, { s: 1.25, ...ph(390, 300) }], // the sent card
    [55.0, 0.8, OUT],
    [59.5, 0.7, { s: 1.4, ...ph(330, 444) }], // 2 changes to your roster
    [62.1, 0.7, { s: 2.0, ...ph(391, 758) }], // Thu 22 Oct
    [65.0, 0.6, { s: 2.0, ...ph(200, 1156) }], // North Gate
    [70.6, 0.5, OUT],
  ]),
  taps: [
    { t: 42.4, ...ph(139, 440) },
    { t: 45.0, ...ph(389, 580) },
    { t: 48.3, ...ph(390, 1486) },
    { t: 61.6, ...ph(330, 444) },
  ],
};

/* The lead's phone: the team, Nicole's request, today, the sick gap, the covered roster. */
const LP = {
  before: p("lp-01-team-before", "The lead's phone: the team roster by day, not solved yet"),
  ping: p("lp-02-request", "The lead's phone: next steps, 1 leave request to review"),
  review: p("lp-02b-review", "The lead's phone: Nicole Ang's leave request with Accept into plan"),
  today: p("lp-06-today", "The lead's phone: today's published roster"),
  short: p("lp-04-sick-ping", "The lead's phone: Thu 22 is 1 seat short"),
  covered: p("lp-05-covered", "The lead's phone: every day covered again"),
  published: p(
    "lp-03-team-published",
    "The lead's phone: the published roster by day, Nicole Ang on leave",
  ),
};

export const LEAD_PHONE: Track = {
  ...PH,
  cuts: [
    { t: 0, page: LP.before, how: "fade" },
    { t: 13.6, page: LP.ping, how: "fade" },
    { t: 16.4, page: LP.review, how: "slide" },
    { t: 39.2, page: LP.today, how: "fade" },
    { t: 49.0, page: LP.short, how: "fade" },
    { t: 58.2, page: LP.covered, how: "fade" },
    { t: 62.8, page: LP.published, how: "slide", still: true },
    { t: 71.5, page: LP.before, how: "fade" },
  ],
  cams: camera([
    [14.0, 0.6, { s: 1.8, ...ph(230, 585) }], // 1 leave request to review
    [16.2, 0.3, OUT],
    [16.6, 0.6, { s: 1.3, ...ph(310, 366) }], // Nicole's request card
    [22.0, 0.6, OUT],
    [39.5, 0.6, { s: 1.6, ...ph(391, 272) }], // today
    [41.5, 0.5, OUT],
    [49.3, 0.6, { s: 1.8, ...ph(262, 166) }], // 13/14, 1 seat short; then 14/14
    [62.0, 0.5, OUT],
    [63.1, 0.8, { s: 1.8, ...ph(250, 1362) }], // Nicole on leave in the published roster
    [70.6, 0.6, OUT],
  ]),
  taps: [{ t: 16.0, ...ph(230, 585) }],
};

/*
 * The lead's laptop: sets the gameplan (a rule, the stations, who may work
 * where), accepts Nicole's request, solves, moves one shift, publishes, then
 * covers the sick day.
 */
const Q = {
  rules: p("g-01-rules", "Rules and fairness: Post-call rest day switched off"),
  rulesOn: p("g-02-rules-on", "Rules and fairness: Post-call rest day switched on, enforced"),
  stations: p("g-03-stations", "Stations with the people each needs per day"),
  people: p("g-04-people", "A person's eligible stations"),
  peopleOn: p("g-05-people-on", "The same person now eligible for Control room day"),
  empty: p("q-00-queue-empty", "The lead's review queue: nothing waiting"),
  arrived: p(
    "q-01-queue-new",
    "Nicole Ang's leave request arrives with Accept into plan, Ask to move and Decline",
  ),
  accepted: p("q-02-accepted", "The request accepted into the plan"),
  unsolved: p(
    "q-03-unsolved",
    "The roster for 12 to 25 October, not solved, with Solve these dates",
  ),
  solving: p("q-04-solving", "The solve running"),
  solved: p("q-05-solved", "The solved roster with its figures"),
  grid: p("r-01-grid", "The roster grid with Colin Yeo's shift on Monday 12 October"),
  move: p("r-02-move", "Cover this slot with Anita Rao, and Apply cover-change"),
  moved: p("r-03-moved", "Anita Rao now holds the shift"),
  publish: p("r-04-publish", "The edited roster with Publish roster"),
  published: p("r-05-published", "Published to 42 members"),
  gap: p("c-01-gap", "Thursday 22 is 1 seat short after a sick report, with Review cover"),
  options: p("c-02-cover", "Shortfall options: Seat Hakim suggested"),
  confirm: p("c-02b-confirm", "Confirm: seat Hakim Salleh on North gate"),
  covered: p("c-03-covered", "Every day covered again, 14 of 14"),
};

export const LEAD_TRACK: Track = {
  ...LD,
  cuts: [
    { t: 0, page: Q.rules, how: "fade" },
    { t: 3.1, page: Q.rulesOn, how: "fade" },
    { t: 4.4, page: Q.stations, how: "slide" },
    { t: 8.0, page: Q.people, how: "slide" },
    { t: 10.6, page: Q.peopleOn, how: "fade" },
    { t: 11.8, page: Q.empty, how: "slide" },
    { t: 13.6, page: Q.arrived, how: "fade" },
    { t: 17.8, page: Q.accepted, how: "fade" },
    { t: 20.4, page: Q.unsolved, how: "slide" },
    { t: 23.0, page: Q.solving, how: "fade" },
    { t: 25.8, page: Q.solved, how: "fade" },
    { t: 28.7, page: Q.grid, how: "fade" },
    { t: 31.3, page: Q.move, how: "fade" },
    { t: 33.7, page: Q.moved, how: "fade" },
    { t: 36.5, page: Q.publish, how: "fade" },
    { t: 39.0, page: Q.published, how: "fade" },
    { t: 49.0, page: Q.gap, how: "fade" },
    { t: 53.2, page: Q.options, how: "slide" },
    { t: 55.8, page: Q.confirm, how: "fade" },
    { t: 58.1, page: Q.covered, how: "fade", still: true },
    { t: 71.5, page: Q.rules, how: "fade" },
  ],
  cams: camera([
    [0.6, 0.7, { s: 2.2, ...ld(578, 277) }], // Post-call rest day switch
    [3.6, 0.4, OUT],
    [4.8, 0.7, { s: 1.8, ...ld(620, 320) }], // stations: need per day
    [7.6, 0.4, OUT],
    [8.3, 0.7, { s: 2.2, ...ld(96, 563) }], // Control room day chip
    [11.4, 0.4, OUT],
    [13.8, 0.6, { s: 1.8, ...ld(300, 420) }], // Nicole's request arrives
    [15.7, 0.5, { s: 2.2, ...ld(139, 500) }], // pan to Accept into plan
    [18.0, 0.5, { s: 2.2, ...ld(385, 299) }], // you accepted this
    [19.9, 0.4, OUT],
    [20.7, 0.7, { s: 2.2, ...ld(861, 370) }], // Solve these dates
    [23.2, 0.6, { s: 1.6, ...ld(837, 330) }], // the solve running
    [25.3, 0.4, OUT],
    [26.0, 0.7, { s: 1.6, ...ld(330, 290) }], // the figures
    [28.2, 0.4, OUT],
    [29.0, 0.7, { s: 2.2, ...ld(397, 413) }], // Colin Yeo's shift
    [31.5, 0.6, { s: 2.2, ...ld(936, 413) }], // Apply cover-change
    [33.9, 0.6, { s: 2.2, ...ld(397, 675) }], // now Anita Rao's
    [36.0, 0.4, OUT],
    [36.8, 0.6, { s: 2.2, ...ld(981, 404) }], // Publish roster
    [39.2, 0.6, { s: 2.2, ...ld(972, 362) }], // Published to 42 members.
    [42.0, 0.6, OUT],
    [49.2, 0.5, { s: 1.8, ...ld(151, 207) }], // 13/14, short on Thu 22
    [51.0, 0.5, { s: 2.2, ...ld(964, 278) }], // Review cover
    [53.5, 0.6, { s: 2.0, ...ld(1014, 221) }], // Seat Hakim
    [56.0, 0.5, { s: 2.2, ...ld(773, 297) }], // confirm
    [58.3, 0.6, { s: 1.8, ...ld(151, 207) }], // 14/14
    [66.0, 1.0, OUT],
  ]),
  taps: [
    { t: 2.9, ...ld(578, 277) },
    { t: 10.4, ...ld(96, 563) },
    { t: 17.6, ...ld(139, 500) },
    { t: 22.8, ...ld(861, 370) },
    { t: 31.1, ...ld(397, 413) },
    { t: 33.5, ...ld(936, 413) },
    { t: 38.8, ...ld(981, 404) },
    { t: 53.0, ...ld(964, 278) },
    { t: 55.6, ...ld(1014, 221) },
    { t: 57.9, ...ld(773, 297) },
  ],
};

/* ---- Keyframes for the Web Animations API ---- */

export type Frame = { offset: number; easing?: string } & Record<string, string | number>;

const off = (t: number) => Math.min(1, Math.max(0, t / LOOP_S));

/** The camera transform that puts (fx, fy) at the view centre, clamped so no edge shows. */
export function cameraTransform(c: Omit<Cam, "t">, view: number): string {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  const x = clamp(view / 2 - c.s * c.fx, view - c.s, 0) * 100;
  const y = clamp(0.5 - c.s * c.fy, 1 - c.s, 0) * 100;
  return `translate(${x.toFixed(2)}%, ${y.toFixed(2)}%) scale(${c.s})`;
}

export function cameraFrames(track: Track): Frame[] {
  return track.cams.map((c, i) => {
    const next = track.cams[i + 1];
    const moving = next !== undefined && (next.s !== c.s || next.fx !== c.fx || next.fy !== c.fy);
    return {
      offset: off(c.t),
      transform: cameraTransform(c, track.view),
      easing: moving ? EASE : "linear",
    };
  });
}

const FADE_S = 0.4;
const SLIDE_S = 0.45;

/**
 * One layer per cut, in cut order, so a later page always sits on top. A
 * layer is hidden until its cut, comes in, and drops out once the next page
 * has fully covered it.
 */
export function layerFrames(track: Track, i: number): Frame[] {
  const cut = track.cuts[i];
  if (!cut) return [];
  const next = track.cuts[i + 1];
  const enter = cut.how === "fade" ? FADE_S : SLIDE_S;
  const hidden =
    cut.how === "slide" ? "translateX(100%)" : cut.how === "rise" ? "translateY(12%)" : "none";
  const leave = next ? next.t + (next.how === "fade" ? FADE_S : SLIDE_S) : LOOP_S;
  const frames: Frame[] = [];
  if (cut.t <= 0) {
    frames.push({ offset: 0, opacity: 1, transform: "none" });
  } else {
    frames.push({ offset: 0, opacity: 0, transform: hidden });
    frames.push({
      offset: off(cut.t),
      opacity: cut.how === "slide" ? 1 : 0,
      transform: hidden,
      easing: EASE,
    });
    frames.push({ offset: off(cut.t + enter), opacity: 1, transform: "none" });
  }
  if (leave < LOOP_S) {
    frames.push({ offset: off(leave), opacity: 1, transform: "none" });
    frames.push({ offset: off(leave + 0.01), opacity: 0, transform: "none" });
  }
  frames.push({ offset: 1, opacity: frames[frames.length - 1]?.opacity ?? 0, transform: "none" });
  return frames;
}

const TAP_S = 0.55;

export function tapFrames(tap: Tap): Frame[] {
  return [
    { offset: 0, opacity: 0, transform: "translate(-50%, -50%) scale(0.3)" },
    { offset: off(tap.t), opacity: 0, transform: "translate(-50%, -50%) scale(0.3)" },
    { offset: off(tap.t + 0.08), opacity: 0.95, transform: "translate(-50%, -50%) scale(0.55)" },
    { offset: off(tap.t + TAP_S), opacity: 0, transform: "translate(-50%, -50%) scale(1.5)" },
    { offset: 1, opacity: 0, transform: "translate(-50%, -50%) scale(1.5)" },
  ];
}

/** Seconds the pointer takes to glide to its next tap. */
const GLIDE_S = 0.8;
/** A gap longer than this between taps hides the pointer in between. */
const REST_S = 4;

/**
 * The pointer over a screen (#2788): a mouse on the monitor, a fingertip on
 * a phone. It glides to each tap, presses as the ripple shows, and fades out
 * when the next tap is far off. Positions are in % of the capture, so the
 * pointer rides the camera with the page.
 */
export function cursorFrames(taps: ReadonlyArray<Tap>): Frame[] {
  const sorted = [...taps].sort((a, b) => a.t - b.t);
  const first = sorted[0];
  if (!first) {
    return [
      { offset: 0, opacity: 0 },
      { offset: 1, opacity: 0 },
    ];
  }
  const frames: Frame[] = [];
  let last = 0;
  const at = (t: number, fx: number, fy: number, opacity: number, scale = 1) => {
    const offset = Math.max(last, off(t));
    last = offset;
    frames.push({
      offset,
      left: `${(fx * 100).toFixed(2)}%`,
      top: `${(fy * 100).toFixed(2)}%`,
      opacity,
      transform: `scale(${scale})`,
    });
  };
  at(0, first.fx + 0.08, first.fy + 0.1, 0);
  let prev: Tap | null = null;
  for (const tap of sorted) {
    if (!prev || tap.t - prev.t > REST_S) {
      if (prev) at(prev.t + 1.2, prev.fx, prev.fy, 0);
      at(tap.t - GLIDE_S - 0.4, tap.fx + 0.08, tap.fy + 0.1, 0);
    } else {
      at(Math.max(prev.t + 0.35, tap.t - GLIDE_S), prev.fx, prev.fy, 1);
    }
    at(tap.t - 0.05, tap.fx, tap.fy, 1);
    at(tap.t + 0.08, tap.fx, tap.fy, 1, 0.82);
    at(tap.t + 0.3, tap.fx, tap.fy, 1);
    prev = tap;
  }
  if (prev) at(prev.t + 1.2, prev.fx, prev.fy, 0);
  const end = frames[frames.length - 1];
  if (end) frames.push({ ...end, offset: 1 });
  return frames;
}
